<template>
  <div>
    <b-card class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("AUDITS") }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar">
          <v-dialog :retain-focus="false" v-model="dialogXLS" max-width="900px">
            <template v-slot:activator="{ on, attrs }">
              <v-progress-circular
                v-show="isDialogXLS"
                indeterminate
                color="green"
              ></v-progress-circular>
              <v-btn
                v-show="!isDialogXLS"
                color="btn-success"
                class="btn btn-light-success font-weight-bolder mr-2"
                v-bind="attrs"
                v-on="on"
              >
                <i class="flaticon-download"></i>
                {{ $t("DOWNLOAD_XLS_ANALYTIC_REVERSE_PAY_SHEET") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{
                  $t("DOWNLOAD_XLS_ANALYTIC_REVERSE_PAY_SHEET")
                }}</span>
              </v-card-title>

              <v-card-text>
                <b-col>
                  <FormDatepicker
                    v-model="dates"
                    :type="'text'"
                    :clearable="true"
                    :i18n="{
                      label: 'DATE'
                    }"
                  ></FormDatepicker>
                </b-col>
                <b-col>
                  <v-autocomplete
                    v-if="_.size(stores) > 0"
                    :items="stores"
                    :label="$t('STORE')"
                    item-text="name"
                    item-value="id"
                    clearable
                    @click:clear="clearFilter('storeId')"
                    v-model="filters.storeId"
                  ></v-autocomplete>
                </b-col>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="close">
                  Cancel
                </v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  @click="getAnalyticReversePaySheetXLS(2)"
                >
                  Генерирай
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <router-link
            v-permission="['stock-audits-create']"
            :to="{ name: 'stock-audit-edit-last' }"
            class="btn btn-outline-success font-weight-bolder mr-2"
            ><i class="flaticon2-pen"></i>
            {{ $t("EDIT_LAST_AUDIT") }}
          </router-link>
          <router-link
            v-permission="['stock-audits-create']"
            to="add"
            class="btn btn-light-success font-weight-bolder mr-2"
            ><i class="flaticon2-plus"></i>
            {{ $t("ADD_NEW") }}
          </router-link>
        </div>
      </template>
      <v-card v-show="!isPageLoading">
        <SearchTools
          :searchQuery.sync="searchQuery"
          @do-search="doSearch"
          @do-clear="doClear"
        >
          <template v-slot:prepend>
            <b-col :sm="6">
              <FormDatepickerBase
                :type="'text'"
                :clearable="true"
                :max="maxDate"
                :i18n="{
                  label: 'FROM',
                  placeholder: $t('FROM')
                }"
                :date.sync="filters.fromDate"
              ></FormDatepickerBase>
            </b-col>

            <b-col :sm="6">
              <FormDatepickerBase
                :type="'text'"
                :clearable="true"
                :max="maxDate"
                :i18n="{
                  label: 'TO',
                  placeholder: $t('TO')
                }"
                :date.sync="filters.toDate"
              ></FormDatepickerBase>
            </b-col>
            <b-col :lg="4" :md="12" v-if="!isPageLoading && _.size(stores) > 0">
              <v-autocomplete
                :items="stores"
                :label="$t('STORE')"
                item-text="name"
                item-value="id"
                clearable
                hide-details
                @click:clear="clearFilter('storeId')"
                v-model="filters.storeId"
              ></v-autocomplete>
            </b-col>
          </template>
        </SearchTools>
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          sort-by="forDate"
          sort-desc="true"
          :loading="isLoadingStock"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right',
            itemsPerPageText: $t('ITEMS_PER_PAGE_TEXT')
          }"
          @update:options="getDataFromApi()"
          class="elevation-1"
        >
          <template v-slot:item.forDate="{ item }">
            <router-link
              v-if="checkIfUserHasPermission(['stock-audits-show'])"
              :to="{ name: 'stock-audit-review', params: { id: item.id } }"
            >
              {{ item.forDate | formatDate }}
            </router-link>
            <template v-else>{{ item.forDate | formatDate }}</template>
          </template>

          <template v-slot:item.actions="{ item }">
            <ActionButton
              :to="{ name: 'stock-audit-review', params: { id: item.id } }"
              :permissions="['stock-audits-show']"
              :label="$t('VIEW')"
              :icon="'mdi-format-list-bulleted'"
            />
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
      <v-overlay :value="!firstLoader && isLoadingSharedStore">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <template v-slot:footer></template>
    </b-card>
  </div>
</template>

<script>
//General
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

import { mapGetters } from "vuex";
import SearchTools from "@/core/components/table/SearchTools.vue";
import ActionButton from "@/core/components/table/ActionButton.vue";
import FormDatepickerBase from "@/view/content/forms/components/FormDatepickerBase.vue";

//Audit Module
import {
  EXPORT_ANALYTIC_REVERSE_PAY_SHEET_XLS,
  FETCH_STOCK_AUDITS
} from "@/modules/stock/store/stock.module";

//Shared Modules
import { FETCH_STORES_BASIC } from "@/core/services/store/shared_store.module";

import { permissionMixin } from "@/core/mixins/permissionMixin.js";

import moment from "moment";
import FormDatepicker from "@/view/content/forms/components/FormDatepicker.vue";

export default {
  mixins: [permissionMixin],
  data() {
    return {
      dialogXLS: false,
      isDialogXLS: false,
      firstLoader: true,
      isPageLoading: true,
      tableLoading: true,
      dates: [],
      items: [],
      stores: [],
      totalItems: 100,
      searchQuery: "",
      filters: {
        storeId: null,
        fromDate: null,
        toDate: null
      },
      options: {
        descending: true,
        page: 1,
        itemsPerPage: 10,
        totalItems: 100
      },
      rowsPerPageItems: [10, 20, 30, 40, 50],
      columns: {},
      cancelSource: null,
      selectedItemId: null,
      headers: [
        {
          text: "Id",
          align: "left",
          value: "id",
          width: "100px"
        },
        {
          text: this.$t("FOR_DATE"),
          value: "forDate"
        },
        { text: this.$t("STORE"), value: "storeName" },
        { text: this.$t("NAME"), value: "userName" },
        {
          text: this.$t("AUDIT_FOOD_COST"),
          value: "totalFoodCost",
          align: "right"
        },
        {
          align: "right",
          text: this.$t("ACTIONS"),
          value: "actions",
          sortable: false,
          width: "10%"
        }
      ]
    };
  },
  components: {
    FormDatepicker,
    SearchTools,
    ActionButton,
    FormDatepickerBase
  },
  watch: {
    storesParams(value) {
      let vm = this;
      vm.filters.storeId = null;
    }
  },
  mounted() {
    let vm = this;
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Stock Audits", route: { name: "stock-audits-list" } },
      { title: "Stock Audits List" }
    ]);

    vm.getStores();
  },
  computed: {
    ...mapGetters([
      "getItems",
      "isLoadingStock",
      "isLoadingSharedStore",
      "currentUser"
    ]),
    params() {
      return {
        ...this.options,
        query: this.searchQuery,
        storeId: this.filters.storeId,
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate
      };
    },
    storesParams() {
      let storesParams = {};
      return storesParams;
    },
    tableHeaders() {
      let vm = this;

      return vm.headers;
    },
    maxDate() {
      return moment().format("YYYY-MM-DD");
    }
  },
  methods: {
    close() {
      let vm = this;
      vm.dialogXLS = false;
    },
    clearFilter(key) {
      let vm = this;
      vm.$nextTick(function() {
        this.$set(vm.filters, key, null);
      });
    },
    cancelRequest() {
      //Axios cancelSource to stop current search if new value is entered
      if (this.cancelSource) {
        this.cancelSource.cancel("Start new search, stop active search");
      }
    },
    doSearch() {
      let vm = this;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    doClear() {
      let vm = this;
      vm.searchQuery = "";
      vm.filters.storeId = null;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    getDataFromApi() {
      let vm = this;
      // vm.tableLoading = true;
      //copy current params to modify
      let params = this.params;

      params.length = params.itemsPerPage; //set how many records to fecth per page
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1); //set offset
      // console.log(this.params);
      let apiParams = vm.transformUrlParams(params);
      // console.log(apiParams);
      vm.$store
        .dispatch(FETCH_STOCK_AUDITS, apiParams)
        .then(data => {
          // vm.$notify({
          //   group: "notify",
          //   type: "success",
          //   title: "<i class='flaticon2-checkmark'></i> Success",
          //   text: data.message
          // });
          vm.firstLoader = false;
          vm.$nextTick(function() {
            vm.items = data.data.items;
            vm.totalItems = data.data.totalItemsCount;

            if (vm.isPageLoading == true) {
              vm.isPageLoading = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Nout Found"
            });
            this.$router.push({ name: "stock-audits-list" });
          }
        });
    },
    getAnalyticReversePaySheetXLS() {
      let vm = this;
      vm.isDialogXLS = true;
      let params = this.params;
      params.dateRange = vm.dates;
      params.storeId = vm.filters.storeId;
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1); //set offset
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(EXPORT_ANALYTIC_REVERSE_PAY_SHEET_XLS, apiParams)
        .then(data => {
          vm.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });
          vm.firstLoader = false;
          vm.$nextTick(function() {
            let blob = new Blob([data], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = "reverse-analytics-" + vm.dates;
            link.click();
            link.remove();
            if (vm.isDialogXLS == true) {
              vm.dialogXLS = false;
              vm.isDialogXLS = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
          }
        });
    },
    getStores() {
      let vm = this;
      let params = this.storesParams;
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(FETCH_STORES_BASIC, apiParams)
        .then(data => {
          this.$set(vm, "stores", data.data);
        })
        .catch(response => {
          console.log(response);
        });
    },
    transformUrlParams(params) {
      let apiParams =
        "?" +
        Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&");
      return apiParams;
    }
  }
};
</script>
